import React, { useState, useEffect, useRef } from 'react';
import {
  Phone, Loader, Eye, MoreVertical, FileText, History, Edit,
  Filter, X, Tag, Layout, User, Truck, CalendarDays, ChevronDown, Download,
  Upload, CheckCircle, AlertCircle, Clock
} from 'lucide-react';
import ThemeSelect from '../components/ThemeSelect';
import ExpandableSearchBar from '../components/ExpandableSearchBar';
import { ordersApi, courierCredentialApi, productsApi, orderCampaignApi, employeesApi, leadAPIApi, fetchApiDirect } from '../services/api';
import { maskPhone, formatDate, formatCurrency, formatApprovalStatus } from '../utils/helpers';
import icon from "../assets/pngegg2.png"
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { usePermissions } from '../hooks/Usepermissions';
import Pagination from '../components/Pagination';
import ExcelExportButton from '../components/ExcelExportButton';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';

import TotalOrdersIcon from '../assets/Logo2.0Screen/Total Orders.svg';
import CompleteOrdersIcon from '../assets/Logo2.0Screen/Complete Orders.svg';
import ProcessOrdersIcon from '../assets/Logo2.0Screen/Process Orders.svg';
import PendingOrdersIcon from '../assets/Logo2.0Screen/Pending Orders.svg';
import HoldOrdersIcon from '../assets/Logo2.0Screen/Hold Orders.svg';
import RejectedOrdersIcon from '../assets/Logo2.0Screen/Rejected Orders.svg';
import OFDOrdersIcon from '../assets/Logo2.0Screen/OFD Orders.svg';
import DeliveredOrdersIcon from '../assets/Logo2.0Screen/Delivered Orders.svg';
import RTOOrdersIcon from '../assets/Logo2.0Screen/motion.div.svg';
import { BASE_URL as API_URL } from '../services/api';

const { RangePicker } = DatePicker;

const PAGE_SIZE = 20;

// Bulk-import base — services/api.js ke fetchApiDirect jaisa hi base hona chahiye.
// CRA mein env var REACT_APP_ se start hona ZAROORI hai (VITE_ ignore ho jata hai).
const API_BASE =API_URL;
const getAuthToken = () =>
  localStorage.getItem('token') || localStorage.getItem('authToken') || localStorage.getItem('jwtToken') || '';

function OrdersList({ onAddOrder, onViewOrder }) {
  const { currentTheme } = useTheme();
  const navigate = useNavigate();
  const { has, isAdmin } = usePermissions();

  const [orders, setOrders] = useState([]);
  const [allEmployeesList, setAllEmployeesList] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [selectedDateRange, setSelectedDateRange] = useState('today');
  const [selectedDateRangeLabel, setSelectedDateRangeLabel] = useState('Today');
  const [isDateDropdownOpen, setIsDateDropdownOpen] = useState(false);
  const [showCustomDateModal, setShowCustomDateModal] = useState(false);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [bulkUploadFile, setBulkUploadFile] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkJobId, setBulkJobId] = useState(null);
  const [bulkJobStatus, setBulkJobStatus] = useState(null);
  const [bulkPolling, setBulkPolling] = useState(false);
  const bulkPollRef = useRef(null);
  // ── Global phone search ───────────────────────────────────────────────────
  const [isGlobalPhoneSearch, setIsGlobalPhoneSearch] = useState(false);
  const [globalPhoneNumber, setGlobalPhoneNumber] = useState('');

  // ── Filter options — floors/teams/teamEmployees dynamic fetch ─────────────
  const [filterOptions, setFilterOptions] = useState({
    couriers: [], categories: [], states: [], leadSources: [],
    paymentModes: [], campaigns: [], employees: [], products: [],
    floors: [],         // /floors API se aayega
    teams: [],          // floor select pe /employees/grouped?floorId= se aayega
    teamEmployees: [],  // team select pe /employees/grouped?floorId=&teamId= se aayega
  });

  // Loading states for dynamic dropdowns
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [empLoading, setEmpLoading] = useState(false);

  const dateDropdownRef = useRef(null);
  const currentPageRef = useRef(1);

  const selectedStatusRef = useRef(null);
  const activeFiltersRef = useRef(null);
  const selectedDateRangeRef = useRef('today');
  const customStartDateRef = useRef('');
  const customEndDateRef = useRef('');

  const getCurrentUser = () => {
    try { const u = localStorage.getItem('user'); return u ? JSON.parse(u) : null; }
    catch { return null; }
  };

  // ── initialFilters: TL ke liye floorId/teamId pre-filled ────────────────
  const getInitialFilters = () => {
    const u = getCurrentUser();
    const tl = !isAdmin && (u?.designationId === 1 || u?.designationName === 'Team Leader');
    return {
      search: '', orders: 'All Orders', courier: 'All Couriers', courierPartner: 'All Partners',
      date: '', dateType: 'Created Date', category: 'All Categories', status: 'All Status',
      state: 'All States', leadSource: 'All Sources', paymentMode: 'All Modes',
      campaign: 'All Campaigns', orderBy: 'Select User', actionBy: 'Select User',
      teamLead: 'Select TL', product: 'All Products',
      orderById: '', actionById: '', teamLeadId: '',
      // TL ke liye pre-filled, admin ke liye empty
      floorId: tl ? String(u?.floorId || '') : '',
      teamId: tl ? String(u?.teamId || '') : '',
    };
  };
  const initialFilters = getInitialFilters();

  const [activeFilters, setActiveFilters] = useState(initialFilters);
  const [tempFilters, setTempFilters] = useState(initialFilters);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

  useEffect(() => { activeFiltersRef.current = activeFilters; }, [activeFilters]);
  useEffect(() => { selectedStatusRef.current = selectedStatus; }, [selectedStatus]);
  useEffect(() => { selectedDateRangeRef.current = selectedDateRange; }, [selectedDateRange]);
  useEffect(() => { customStartDateRef.current = customStartDate; }, [customStartDate]);
  useEffect(() => { customEndDateRef.current = customEndDate; }, [customEndDate]);

  // ── TL detection helper ───────────────────────────────────────────────────
  const currentUser = getCurrentUser();
  // designationId: 1 = Team Leader (localStorage mein designationName nahi aata)
  const isTL = !isAdmin && (
    currentUser?.designationId === 1 ||
    currentUser?.designationName === 'Team Leader'
  );
  const tlFloorId = isTL ? String(currentUser?.floorId || '') : '';
  const tlTeamId = isTL ? String(currentUser?.teamId || '') : '';
  const tlFloorName = isTL ? (currentUser?.floorName || '') : '';
  const tlTeamName = isTL ? (currentUser?.teamName || '') : '';

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dateDropdownRef.current && !dateDropdownRef.current.contains(event.target))
        setIsDateDropdownOpen(false);
      const menus = document.querySelectorAll('.action-menu-container');
      let inside = false;
      menus.forEach(m => { if (m.contains(event.target)) inside = true; });
      if (!inside) setOpenMenuId(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    fetchOrders(null, 'today', '', '', initialFilters, 0);
    fetchFilterOptions();
    // TL login pe uske team ke employees auto-fetch karo
    if (isTL && tlFloorId && tlTeamId) {
      fetchTLEmployees(tlFloorId, tlTeamId);
    }
  }, []);

  // Mount par ye effect bhi chalta tha, jiski wajah se upar wale mount effect ke
  // saath milakar fetchOrders DO baar hit hota tha. Pehle render ko skip karo.
  const skipFirstSearch = useRef(true);
  useEffect(() => {
    if (!searchTerm.trim()) {
      setIsGlobalPhoneSearch(false);
      setGlobalPhoneNumber('');
    }
    if (skipFirstSearch.current) {
      skipFirstSearch.current = false;
      return;
    }
    const timer = setTimeout(() => {
      setCurrentPage(1);
      currentPageRef.current = 1;
      fetchOrders(
        selectedStatusRef.current,
        selectedDateRangeRef.current,
        customStartDateRef.current,
        customEndDateRef.current,
        activeFiltersRef.current,
        0
      );
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const dateRangeOptions = [
    { value: 'allOrders', label: 'All View' },
    { value: 'today', label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'this_week', label: 'This Week' },
    { value: 'this_month', label: 'This Month' },
    { value: 'this_year', label: 'This Year' },
    { value: 'custom', label: 'Custom Range' },
  ];

  const resolveDateRange = (preset, customStart = '', customEnd = '') => {
    const now = new Date();
    const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const fmtStart = (d) => { d.setHours(0, 0, 0, 0); return fmt(d); };
    const fmtEnd = (d) => { d.setHours(23, 59, 59, 999); return fmt(d); };
    switch (preset) {
      case 'today': return { startDate: fmtStart(new Date(now)), endDate: fmtEnd(new Date(now)) };
      case 'yesterday': { const y = new Date(now); y.setDate(y.getDate() - 1); return { startDate: fmtStart(y), endDate: fmtEnd(new Date(y)) }; }
      case 'this_week': { const d = now.getDay(), diff = now.getDate() - d + (d === 0 ? -6 : 1); const s = new Date(now); s.setDate(diff); return { startDate: fmtStart(s), endDate: fmtEnd(new Date(now)) }; }
      case 'this_month': return { startDate: fmtStart(new Date(now.getFullYear(), now.getMonth(), 1)), endDate: fmtEnd(new Date(now.getFullYear(), now.getMonth() + 1, 0)) };
      case 'this_year': return { startDate: fmtStart(new Date(now.getFullYear(), 0, 1)), endDate: fmtEnd(new Date(now.getFullYear(), 11, 31)) };
      case 'custom': return { startDate: customStart || '', endDate: customEnd || '' };
      default: return { startDate: '', endDate: '' };
    }
  };

  // ── Bulk Upload Functions ─────────────────────────────────────────────────
  const handleBulkFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const allowed = ['text/csv', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel'];
    if (!allowed.includes(file.type) && !file.name.match(/\.(csv|xlsx|xls)$/i)) {
      alert('Only CSV or Excel files are allowed');
      return;
    }
    setBulkUploadFile(file);
    setBulkJobStatus(null);
    setBulkJobId(null);
  };

  const handleBulkUpload = async () => {
    if (!bulkUploadFile) { alert('Please select a file first'); return; }
    setBulkUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', bulkUploadFile);
      formData.append('config', JSON.stringify({
        defaultOrderStatus: 'PROCESSING',
        defaultApprovalStatus: 'UNCONFIRMED',
        skipDuplicates: true,
        chunkSize: 10
      }));

      const token = getAuthToken();
      const res = await fetch(`${API_BASE}/orders/bulk-import`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (!res.ok) throw new Error(`Upload failed: ${res.status}`);
      const data = await res.json();
      const jobId = data.jobId || data.data?.jobId;
      if (!jobId) throw new Error('Job ID not received');

      setBulkJobId(jobId);
      setBulkJobStatus({ status: 'QUEUED', progressPercent: 0, processedRows: 0, totalRows: 0 });
      startBulkPolling(jobId);
    } catch (err) {
      console.error('Bulk upload error:', err);
      alert(`Upload failed: ${err.message}`);
    } finally {
      setBulkUploading(false);
    }
  };

  const startBulkPolling = (jobId) => {
    setBulkPolling(true);
    if (bulkPollRef.current) clearInterval(bulkPollRef.current);

    bulkPollRef.current = setInterval(async () => {
      try {
        const data = await fetchApiDirect(`/orders/bulk-import/${jobId}/status`);
        if (!data) return;
        setBulkJobStatus(data);

        if (['COMPLETED', 'FAILED', 'CANCELLED'].includes(data.status)) {
          clearInterval(bulkPollRef.current);
          setBulkPolling(false);
          if (data.status === 'COMPLETED') {
            fetchOrders(null, selectedDateRangeRef.current, customStartDateRef.current, customEndDateRef.current, activeFiltersRef.current, 0);
          }
        }
      } catch (err) {
        console.error('Polling error:', err);
      }
    }, 2000);
  };

  const handleDownloadTemplate = async () => {
    try {
      const token = getAuthToken();
      const res = await fetch(`${API_BASE}/orders/bulk-import/template/csv`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Template download failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'bulk_order_template.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Template download failed');
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => { if (bulkPollRef.current) clearInterval(bulkPollRef.current); };
  }, []);

  // ── fetchFilterOptions — static data + floors list ───────────────────────
  const fetchFilterOptions = async () => {
    try {
      const [couriersRes, categoriesRes, campaignsRes, employeesRes, productsRes, leadSourcesRes, floorsRes] =
        await Promise.allSettled([
          courierCredentialApi.getAllCredentials(),
          productsApi.getAllCategories(),
          orderCampaignApi.getAllCampaigns(),
          employeesApi.getAllEmployees(),
          productsApi.getAllProducts(),
          leadAPIApi.getAllLeadAPIs(),
          fetchApiDirect('/floors'),   // ← Floors list
        ]);

      const couriers = couriersRes.status === 'fulfilled'
        ? [...new Set((Array.isArray(couriersRes.value) ? couriersRes.value : []).map(c => c.courierName || c.courierType || c.name || c.type).filter(Boolean))].sort()
        : [];
      const catRaw = categoriesRes.status === 'fulfilled' ? categoriesRes.value : [];
      const categories = [...new Set((Array.isArray(catRaw) ? catRaw : []).map(c => typeof c === 'string' ? c : c?.categoryName || c?.name).filter(Boolean))].sort();
      const campRaw = campaignsRes.status === 'fulfilled' ? campaignsRes.value : [];
      const campaigns = [...new Set((Array.isArray(campRaw) ? campRaw : []).map(c => c?.campaignName || c?.name).filter(Boolean))].sort();
      const empRaw = employeesRes.status === 'fulfilled' ? employeesRes.value : [];
      setAllEmployeesList(empRaw);
      const employees = (Array.isArray(empRaw) ? empRaw : []).map(e => ({ name: e.name || e.employeeName || e.fullName, empId: String(e.id || '') })).filter(e => e.name);
      const states = [];
      const paymentModes = ['COD', 'ONLINE', 'CASH'];
      const leadRaw = leadSourcesRes.status === 'fulfilled' ? leadSourcesRes.value : [];
      const leadArr = Array.isArray(leadRaw) ? leadRaw : Array.isArray(leadRaw?.content) ? leadRaw.content : Array.isArray(leadRaw?.data) ? leadRaw.data : [];
      const leadSources = [...new Set(leadArr.map(l => l?.leadSource).filter(Boolean))].sort();
      const prodRaw = productsRes.status === 'fulfilled' ? productsRes.value : [];
      const prodArr = Array.isArray(prodRaw) ? prodRaw : Array.isArray(prodRaw?.content) ? prodRaw.content : Array.isArray(prodRaw?.data) ? prodRaw.data : Array.isArray(prodRaw?.products) ? prodRaw.products : [];
      const products = [...new Set(prodArr.map(p => p?.productName).filter(Boolean))].sort();

      // ── Parse floors ─────────────────────────────────────────────────────
      const floorsRaw = floorsRes.status === 'fulfilled' ? floorsRes.value : [];
      const floorsArr = Array.isArray(floorsRaw) ? floorsRaw
        : Array.isArray(floorsRaw?.data) ? floorsRaw.data
          : Array.isArray(floorsRaw?.content) ? floorsRaw.content
            : [];
      const floors = floorsArr.map(f => ({
        floorId: String(f.id || f.floorId || ''),
        floorName: f.floorName || f.name || `Floor ${f.id || ''}`,
      })).filter(f => f.floorId);

      setFilterOptions(prev => ({
        ...prev,
        couriers, categories, campaigns, employees,
        states, paymentModes, leadSources, products,
        floors,
        teams: [],
        teamEmployees: [],
      }));
    } catch (err) { console.error('fetchFilterOptions error:', err); }
  };

  // ── Floor select → Teams fetch ────────────────────────────────────────────
  const handleFloorChange = async (floorName) => {
    const floor = filterOptions.floors.find(f => f.floorName === floorName);
    const floorId = floor?.floorId || '';

    // Reset team + employee
    setTempFilters(prev => ({ ...prev, floorId, teamId: '', orderBy: 'Select User', orderById: '' }));
    setFilterOptions(prev => ({ ...prev, teams: [], teamEmployees: [] }));

    if (!floorId) return;

    setTeamsLoading(true);
    try {
      const res = await fetchApiDirect(`/employees/grouped?floorId=${floorId}`);
      // Response: { teams: [ { teamId, teamName, count, employees: [...] } ] }
      const teamsRaw = Array.isArray(res?.teams) ? res.teams
        : Array.isArray(res) ? res
          : [];
      const teams = teamsRaw.map(t => ({
        teamId: String(t.teamId || t.id || ''),
        teamName: t.teamName || t.name || '',
      })).filter(t => t.teamId && t.teamName);
      setFilterOptions(prev => ({ ...prev, teams, teamEmployees: [] }));
    } catch (err) {
      console.error('Teams fetch error:', err);
    } finally {
      setTeamsLoading(false);
    }
  };

  // ── Team select → Employees fetch ────────────────────────────────────────
  const handleTeamChange = async (teamName, currentFloorId) => {
    const team = filterOptions.teams.find(t => t.teamName === teamName);
    const teamId = team?.teamId || '';

    // Reset employee only — teamLeadId alag filter hai, mix mat karo
    setTempFilters(prev => ({ ...prev, teamId, orderBy: 'Select User', orderById: '' }));
    setFilterOptions(prev => ({ ...prev, teamEmployees: [] }));

    if (!teamId) return;

    setEmpLoading(true);
    try {
      const params = currentFloorId
        ? `floorId=${currentFloorId}&teamId=${teamId}`
        : `teamId=${teamId}`;
      const res = await fetchApiDirect(`/employees/grouped?${params}`);
      // Response shape: { employees: [...] }  OR  { teams: [{ employees: [...] }] }
      const empsRaw = Array.isArray(res?.employees)
        ? res.employees
        : Array.isArray(res?.teams?.[0]?.employees)
          ? res.teams[0].employees
          : Array.isArray(res)
            ? res
            : [];
      const teamEmployees = empsRaw.map(e => ({
        empId: String(e.id || e.empId || e.employeeId || ''),
        name: e.name || e.empName || e.employeeName || e.fullName || '',
      })).filter(e => e.name);
      setFilterOptions(prev => ({ ...prev, teamEmployees }));
    } catch (err) {
      console.error('Employees fetch error:', err);
    } finally {
      setEmpLoading(false);
    }
  };

  // ── TL ke liye team employees auto-fetch on mount ───────────────────────
  const fetchTLEmployees = async (floorId, teamId) => {
    setEmpLoading(true);
    try {
      // Try 1: grouped API with floorId + teamId
      const res = await fetchApiDirect(`/employees/grouped?floorId=${floorId}&teamId=${teamId}`);
      console.log('TL employees API response:', res);

      let empsRaw = [];
      if (Array.isArray(res?.employees) && res.employees.length > 0) {
        empsRaw = res.employees;
      } else if (Array.isArray(res?.teams) && res.teams.length > 0) {
        // teams array mein se teamId match karo
        const matchedTeam = res.teams.find(t => String(t.teamId || t.id) === String(teamId));
        empsRaw = matchedTeam?.employees || res.teams[0]?.employees || [];
      } else if (Array.isArray(res)) {
        empsRaw = res;
      }

      // Agar grouped se nahi mila to flat list try karo
      if (empsRaw.length === 0) {
        console.log('Grouped empty, trying flat list...');
        const flatRes = await fetchApiDirect(`/employees/floor/${floorId}`);
        const flatArr = Array.isArray(flatRes) ? flatRes
          : Array.isArray(flatRes?.data) ? flatRes.data
            : Array.isArray(flatRes?.content) ? flatRes.content
              : [];
        // teamId se filter karo (agar teamId field available hai)
        empsRaw = flatArr.filter(e =>
          !teamId || String(e.teamId || e.team?.id || '') === String(teamId)
        );
        // Agar teamId filter ke baad bhi empty to sab lo
        if (empsRaw.length === 0) empsRaw = flatArr;
      }

      const teamEmployees = empsRaw.map(e => ({
        empId: String(e.id || e.empId || e.employeeId || ''),
        name: e.name || e.empName || e.employeeName || e.fullName || '',
      })).filter(e => e.name);

      console.log('TL teamEmployees final:', teamEmployees);
      setFilterOptions(prev => ({ ...prev, teamEmployees }));
    } catch (err) {
      console.error('TL employees fetch error:', err);
    } finally {
      setEmpLoading(false);
    }
  };

  const handleDateRangeFilter = (rangeType, label) => {
    if (rangeType === 'custom') { setIsDateDropdownOpen(false); setShowCustomDateModal(true); return; }
    setSelectedDateRange(rangeType); selectedDateRangeRef.current = rangeType;
    setSelectedDateRangeLabel(label || 'All View');
    setSelectedStatus(null); selectedStatusRef.current = null;
    setIsDateDropdownOpen(false);
    setCurrentPage(1); currentPageRef.current = 1;
    const { startDate, endDate } = resolveDateRange(rangeType);
    fetchOrders(null, rangeType, startDate, endDate, activeFiltersRef.current, 0);
  };

  const handleCustomDateApply = () => {
    if (!customStartDate || !customEndDate) { alert("Please select both dates."); return; }
    if (customEndDate < customStartDate) { alert("End date must be >= start date."); return; }
    const lbl = `${new Date(customStartDate).toLocaleDateString('en-GB')} - ${new Date(customEndDate).toLocaleDateString('en-GB')}`;
    setSelectedDateRange('custom'); selectedDateRangeRef.current = 'custom';
    setSelectedDateRangeLabel(lbl);
    setSelectedStatus(null); selectedStatusRef.current = null;
    setCurrentPage(1); currentPageRef.current = 1;
    setShowCustomDateModal(false);
    fetchOrders(null, 'custom', customStartDate, customEndDate, activeFiltersRef.current, 0);
  };

  const fetchOrders = async (
    forcedStatus = undefined,
    overrideDateRange = undefined,
    overrideStart = undefined,
    overrideEnd = undefined,
    overrideFilters = undefined,
    overridePage = undefined,
  ) => {
    setLoading(true);
    try {
      const user = getCurrentUser();
      if (!user?.id) { setOrders([]); setLoading(false); return; }

      const currentStatus = forcedStatus !== undefined ? forcedStatus : selectedStatusRef.current;
      const currentFilters = overrideFilters !== undefined ? overrideFilters : activeFiltersRef.current || initialFilters;
      const page = overridePage !== undefined ? overridePage : currentPageRef.current - 1;
      const empId = user.id;

      const dateRange = overrideDateRange !== undefined ? overrideDateRange : selectedDateRangeRef.current;
      const startInput = overrideStart !== undefined ? overrideStart : customStartDateRef.current;
      const endInput = overrideEnd !== undefined ? overrideEnd : customEndDateRef.current;

      const resolved = (dateRange && dateRange !== 'allOrders')
        ? resolveDateRange(dateRange, startInput, endInput)
        : { startDate: '', endDate: '' };

      const dateParams = (resolved.startDate && resolved.endDate) ? resolved : null;
      const start = dateParams?.startDate || '';
      const end = dateParams?.endDate || '';

      // ── Summary ─────────────────────────────────────────────────────────
      const summaryParams = new URLSearchParams();
      // user = fetchOrders ke andar fresh getCurrentUser() — isTL yahan se derive karo
      const isTLInFetch = user.role !== 'ADMIN' && (user?.designationId === 1 || user?.designationName === 'Team Leader');
      if (isTLInFetch) {
        const fId = currentFilters?.floorId || String(user?.floorId || '');
        const tId = currentFilters?.teamId || String(user?.teamId || '');
        if (fId) summaryParams.append('floorId', fId);
        if (tId) summaryParams.append('teamId', tId);
      } else if (user.role !== 'ADMIN') {
        summaryParams.append('employeeId', empId);
      }
      if (start) {
        summaryParams.append('startDate', start);
        summaryParams.append('endDate', end);
        summaryParams.append('dateType', 'createdDate');
      }
      if (currentFilters?.orderById) summaryParams.append('orderBy', currentFilters.orderById);
      if (currentFilters?.floorId) summaryParams.append('floorId', currentFilters.floorId);
      if (currentFilters?.teamId) summaryParams.append('teamId', currentFilters.teamId);
      if (currentFilters?.teamLeadId) summaryParams.append('teamLeadId', currentFilters.teamLeadId);
      if (currentFilters?.state !== 'All States') summaryParams.append('state', currentFilters.state);
      if (currentFilters?.leadSource !== 'All Sources') summaryParams.append('source', currentFilters.leadSource);
      if (currentFilters?.paymentMode !== 'All Modes') summaryParams.append('paymentMode', currentFilters.paymentMode);
      if (currentFilters?.campaign !== 'All Campaigns') summaryParams.append('campaignName', currentFilters.campaign);
      if (currentFilters?.courier !== 'All Couriers') summaryParams.append('courier', currentFilters.courier);
      if (currentFilters?.category !== 'All Categories') summaryParams.append('category', currentFilters.category);
      if (currentFilters?.orders !== 'All Orders') summaryParams.append('orderType', currentFilters.orders);
      if (currentFilters?.product !== 'All Products') summaryParams.append('productName', currentFilters.product);
      if (currentStatus) summaryParams.append('status', currentStatus);

      let summaryData = null;
      const summaryQ = summaryParams.toString() ? `?${summaryParams.toString()}` : '';
      const summaryPromise = fetchApiDirect(`/orders${summaryQ}`)
        .then(res => {
          if (res && typeof res === 'object') {
            summaryData = res;
            setSummary({
              totalOrders: res.totalCount || 0,
              confirmedCount: res.confirmedCount || 0,
              completeOrders: res.completeCount || 0,
              confirmedOrders: res.confirmedCount || 0,
              deliveredOrders: res.deliveredCount || 0,
              draftOrders: res.draftCount || 0,
              holdOrders: res.holdCount || 0,
              inTransitOrders: res.inTransitCount || 0,
              ofdOrders: res.ofdCount || 0,
              packedOrders: res.packedCount || 0,
              pendingOrders: res.pendingCount || 0,
              processOrders: res.processingCount || 0,
              readyToShipOrders: res.readyToShipCount || 0,
              rejectedOrders: res.rejectedCount || 0,
              rtoOrders: res.rtoCount || 0,
              verifiedOrders: res.verifiedCount || 0,
              assignedToVerified: res.assignedToVerifiedCount || 0,
            });
          }
        })
        .catch(err => console.error('Summary error:', err));

      let data;

      // ── Phone number? → smart-search API ────────────────────────────────
      const isPhoneNumber = /^[6-9]\d{9}$/.test(searchTerm.trim());

      if (isPhoneNumber) {
        const smartParams = new URLSearchParams();
        smartParams.append('searchTerm', searchTerm.trim());
        data = await fetchApiDirect(`/orders?${smartParams.toString()}`);
        const phoneFlag = true;
        setIsGlobalPhoneSearch(phoneFlag);
        setGlobalPhoneNumber(phoneFlag ? searchTerm.trim() : '');

      } else if (searchTerm) {
        setIsGlobalPhoneSearch(false);
        setGlobalPhoneNumber('');
        const searchParams = new URLSearchParams();
        searchParams.append('searchTerm', searchTerm.trim());
        if (user.role !== 'ADMIN') searchParams.append('employeeId', empId);
        searchParams.append('page', page);
        searchParams.append('size', PAGE_SIZE);
        data = await fetchApiDirect(`/orders?${searchParams.toString()}`);

      } else if (user.role === 'ADMIN') {
        const adminParams = new URLSearchParams();
        if (currentStatus) {
          if (currentStatus === 'PENDING') adminParams.append('status', 'APPROVAL_PENDING');
          else if (currentStatus === 'REJECTED') adminParams.append('status', 'REJECTED');
          else adminParams.append('status', currentStatus);
        }
        if (currentFilters?.leadSource !== 'All Sources') adminParams.append('source', currentFilters.leadSource);
        if (currentFilters?.paymentMode !== 'All Modes') adminParams.append('paymentMode', currentFilters.paymentMode);
        if (currentFilters?.campaign !== 'All Campaigns') adminParams.append('campaignName', currentFilters.campaign);
        if (currentFilters?.state !== 'All States') adminParams.append('state', currentFilters.state);
        if (currentFilters?.orderById) adminParams.append('orderBy', currentFilters.orderById);
        if (currentFilters?.floorId) adminParams.append('floorId', currentFilters.floorId);
        if (currentFilters?.teamId) adminParams.append('teamId', currentFilters.teamId);
        if (currentFilters?.teamLeadId) adminParams.append('teamLeadId', currentFilters.teamLeadId);
        if (currentFilters?.courier !== 'All Couriers') adminParams.append('courier', currentFilters.courier);
        if (currentFilters?.category !== 'All Categories') adminParams.append('category', currentFilters.category);
        if (currentFilters?.orders !== 'All Orders') adminParams.append('orderType', currentFilters.orders);
        if (currentFilters?.product !== 'All Products') adminParams.append('productName', currentFilters.product);
        if (start) {
          adminParams.append('startDate', start);
          adminParams.append('endDate', end);
          adminParams.append('dateType', 'createdDate');
        }
        adminParams.append('page', page);
        adminParams.append('size', PAGE_SIZE);
        data = await fetchApiDirect(`/orders?${adminParams.toString()}`);

      } else {
        const empParams = new URLSearchParams();
        // TL ke liye floorId + teamId bhejo, normal employee ke liye employeeId
        if (isTLInFetch) {
          const fId = currentFilters?.floorId || String(user?.floorId || '');
          const tId = currentFilters?.teamId || String(user?.teamId || '');
          if (fId) empParams.append('floorId', fId);
          if (tId) empParams.append('teamId', tId);
          // TL ne koi specific employee select kiya ho
          if (currentFilters?.orderById) empParams.append('orderBy', currentFilters.orderById);
        } else {
          empParams.append('employeeId', empId);
        }
        empParams.append('page', page);
        empParams.append('size', PAGE_SIZE);
        if (start) {
          empParams.append('startDate', start);
          empParams.append('endDate', end);
          empParams.append('dateType', 'createdDate');
        }
        if (currentFilters?.category !== 'All Categories') empParams.append('category', currentFilters.category);
        if (currentFilters?.state !== 'All States') empParams.append('state', currentFilters.state);
        if (currentFilters?.leadSource !== 'All Sources') empParams.append('source', currentFilters.leadSource);
        if (currentFilters?.paymentMode !== 'All Modes') empParams.append('paymentMode', currentFilters.paymentMode);
        if (currentFilters?.campaign !== 'All Campaigns') empParams.append('campaignName', currentFilters.campaign);
        if (currentFilters?.courier !== 'All Couriers') empParams.append('courier', currentFilters.courier);
        if (currentFilters?.orders !== 'All Orders') empParams.append('orderType', currentFilters.orders);
        if (currentFilters?.product !== 'All Products') empParams.append('productName', currentFilters.product);
        switch (currentStatus) {
          case 'PENDING': empParams.set('status', 'APPROVAL_PENDING'); data = await fetchApiDirect(`/orders?${empParams.toString()}`); break;
          case 'REJECTED': empParams.set('status', 'REJECTED'); data = await fetchApiDirect(`/orders?${empParams.toString()}`); break;
          case 'PROCESSING': empParams.set('status', 'PROCESSING'); data = await fetchApiDirect(`/orders?${empParams.toString()}`); break;
          case 'ON_HOLD': empParams.set('status', 'ON_HOLD'); data = await fetchApiDirect(`/orders?${empParams.toString()}`); break;
          case 'RTO': empParams.set('status', 'RTO'); data = await fetchApiDirect(`/orders?${empParams.toString()}`); break;
          case 'VERIFIED': empParams.set('status', 'VERIFIED'); data = await fetchApiDirect(`/orders?${empParams.toString()}`); break;
          case 'ASSIGN_TO_VERIFIED': empParams.set('status', 'ASSIGN_TO_VERIFIED'); data = await fetchApiDirect(`/orders?${empParams.toString()}`); break;
          case 'CONFIRMED': empParams.set('status', 'CONFIRMED'); data = await fetchApiDirect(`/orders?${empParams.toString()}`); break;
          case 'DRAFT': empParams.set('status', 'DRAFT'); data = await fetchApiDirect(`/orders?${empParams.toString()}`); break;
          case 'PACKED': empParams.set('status', 'PACKED'); data = await fetchApiDirect(`/orders?${empParams.toString()}`); break;
          case 'READY_TO_SHIP': empParams.set('status', 'READY_TO_SHIP'); data = await fetchApiDirect(`/orders?${empParams.toString()}`); break;
          case 'IN_TRANSIT': empParams.set('status', 'IN_TRANSIT'); data = await fetchApiDirect(`/orders?${empParams.toString()}`); break;
          case 'OUT_FOR_DELIVERY': empParams.set('status', 'OUT_FOR_DELIVERY'); data = await fetchApiDirect(`/orders?${empParams.toString()}`); break;
          case 'DELIVERED': empParams.set('status', 'DELIVERED'); data = await fetchApiDirect(`/orders?${empParams.toString()}`); break;
          default: data = await fetchApiDirect(`/orders?${empParams.toString()}`);
        }
      }

      await summaryPromise;

      const finalData = Array.isArray(data) ? data
        : Array.isArray(data?.orders) ? data.orders
          : Array.isArray(data?.content) ? data.content
            : Array.isArray(data?.data) ? data.data
              : [];

      if (isPhoneNumber) {
        setSummary({
          totalOrders: data.totalCount || 0,
          confirmedCount: data.confirmedCount || 0,
          completeOrders: data.completeCount || 0,
          confirmedOrders: data.confirmedCount || 0,
          deliveredOrders: data.deliveredCount || 0,
          draftOrders: data.draftCount || 0,
          holdOrders: data.holdCount || 0,
          inTransitOrders: data.inTransitCount || 0,
          ofdOrders: data.ofdCount || 0,
          packedOrders: data.packedCount || 0,
          pendingOrders: data.pendingCount || data.approvalPendingCount || 0,
          processOrders: data.processingCount || data.processCount || 0,
          readyToShipOrders: data.readyToShipCount || 0,
          rejectedOrders: data.rejectedCount || 0,
          rtoOrders: data.rtoCount || 0,
          verifiedOrders: data.verifiedCount || 0,
          assignedToVerified: data.assignedToVerifiedCount || 0,
        });
      }

      const rawTotal = data?.totalCount ?? data?.totalElements ?? data?.total ?? finalData.length;
      let total = rawTotal;

      if (!isPhoneNumber && currentStatus && summaryData) {
        // Override buggy backend totalCount using the fresh summaryData counts
        switch (currentStatus) {
          case 'CONFIRMED': total = summaryData.confirmedCount ?? total; break;
          case 'VERIFIED': total = summaryData.verifiedCount ?? total; break;
          case 'PROCESSING': total = summaryData.processingCount ?? total; break;
          case 'PENDING': total = summaryData.pendingCount ?? total; break;
          case 'ON_HOLD': total = summaryData.holdCount ?? total; break;
          case 'DRAFT': total = summaryData.draftCount ?? total; break;
          case 'PACKED': total = summaryData.packedCount ?? total; break;
          case 'READY_TO_SHIP': total = summaryData.readyToShipCount ?? total; break;
          case 'IN_TRANSIT': total = summaryData.inTransitCount ?? total; break;
          case 'OUT_FOR_DELIVERY': total = summaryData.ofdCount ?? total; break;
          case 'DELIVERED': total = summaryData.deliveredCount ?? total; break;
          case 'REJECTED': total = summaryData.rejectedCount ?? total; break;
          case 'RTO': total = summaryData.rtoCount ?? total; break;
          default: break;
        }
      }

      if (finalData.length > 0) {
        console.log('CATEGORY_FIELD:', finalData[0].category, finalData[0].categoryName, finalData[0].productCategory);
        console.log('ALL_KEYS:', Object.keys(finalData[0]).join(', '));
      }
      setOrders(finalData);
      setTotalCount(total);

    } catch (err) {
      console.error('fetchOrders error:', err);
      setOrders([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const stats = [
    { label: 'TOTAL ORDERS', value: summary?.totalOrders || '0', color: 'blue', status: null, icon: <img src={TotalOrdersIcon} alt="Total" className="w-full h-full object-contain" /> },
    { label: 'CONFIRMED ORDERS', value: summary?.confirmedCount || '0', color: 'blue', status: 'CONFIRMED', icon: <img src={CompleteOrdersIcon} alt="Verified" className="w-full h-full object-contain" /> },
    { label: 'VERIFIED ORDERS', value: summary?.verifiedOrders || '0', color: 'green', status: 'VERIFIED', icon: <img src={CompleteOrdersIcon} alt="Verified" className="w-full h-full object-contain" /> },
    { label: 'PROCESS ORDERS', value: summary?.processOrders || '0', color: 'yellow', status: 'PROCESSING', icon: <img src={ProcessOrdersIcon} alt="Process" className="w-full h-full object-contain" /> },
    { label: 'PENDING ORDERS', value: summary?.pendingOrders || '0', color: 'orange', status: 'PENDING', icon: <img src={PendingOrdersIcon} alt="Pending" className="w-full h-full object-contain" /> },
    { label: 'HOLD ORDERS', value: summary?.holdOrders || '0', color: 'black', status: 'ON_HOLD', icon: <img src={HoldOrdersIcon} alt="Hold" className="w-full h-full object-contain" /> },
    { label: 'DRAFT ORDERS', value: summary?.draftOrders || '0', color: 'purple', status: 'DRAFT', icon: <img src={PendingOrdersIcon} alt="Draft" className="w-full h-full object-contain" /> },
    { label: 'PACKED ORDERS', value: summary?.packedOrders || '0', color: 'cyan', status: 'PACKED', icon: <img src={ProcessOrdersIcon} alt="Packed" className="w-full h-full object-contain" /> },
    { label: 'READY TO SHIP', value: summary?.readyToShipOrders || '0', color: 'yellow', status: 'READY_TO_SHIP', icon: <img src={ProcessOrdersIcon} alt="Ready" className="w-full h-full object-contain" /> },
    { label: 'IN TRANSIT ORDERS', value: summary?.inTransitOrders || '0', color: 'purple', status: 'IN_TRANSIT', icon: <img src={RTOOrdersIcon} alt="InTransit" className="w-full h-full object-contain" /> },
    { label: 'OFD ORDERS', value: summary?.ofdOrders || '0', color: 'cyan', status: 'OUT_FOR_DELIVERY', icon: <img src={OFDOrdersIcon} alt="OFD" className="w-full h-full object-contain" /> },
    { label: 'DELIVERED ORDERS', value: summary?.deliveredOrders || '0', color: 'teal', status: 'DELIVERED', icon: <img src={DeliveredOrdersIcon} alt="Delivered" className="w-full h-full object-contain" /> },
    { label: 'REJECTED ORDERS', value: summary?.rejectedOrders || '0', color: 'pink', status: 'REJECTED', icon: <img src={RejectedOrdersIcon} alt="Rejected" className="w-full h-full object-contain" /> },
    { label: 'RTO ORDERS', value: summary?.rtoOrders || '0', color: 'purple', status: 'RTO', icon: <img src={RTOOrdersIcon} alt="RTO" className="w-full h-full object-contain" /> },
  ];

  const handleStatClick = (status) => {
    const newStatus = selectedStatus === status ? null : status;
    setSelectedStatus(newStatus); selectedStatusRef.current = newStatus;
    setCurrentPage(1); currentPageRef.current = 1;
    fetchOrders(newStatus, selectedDateRangeRef.current, customStartDateRef.current, customEndDateRef.current, activeFiltersRef.current, 0);
  };

  // ── Backend Excel Export — wahi filters jo table pe lage hain ──────────────
  const handleDownloadExcel = async () => {
    try {
      setIsDownloading(true);
      const user = getCurrentUser();
      if (!user?.id) return;

      const currentStatus = selectedStatusRef.current;
      const currentFilters = activeFiltersRef.current || initialFilters;
      const dateRange = selectedDateRangeRef.current;
      const startInput = customStartDateRef.current;
      const endInput = customEndDateRef.current;
      const resolved = (dateRange && dateRange !== 'allOrders')
        ? resolveDateRange(dateRange, startInput, endInput)
        : { startDate: '', endDate: '' };
      const start = resolved.startDate || '';
      const end = resolved.endDate || '';

      const params = new URLSearchParams();

      // Status
      if (currentStatus) {
        if (currentStatus === 'PENDING') params.append('status', 'APPROVAL_PENDING');
        else params.append('status', currentStatus);
      }

      // Date
      if (start) {
        params.append('startDate', start);
        params.append('endDate', end);
        params.append('dateType', 'createdDate');
      }

      // Search
      if (searchTerm) params.append('searchTerm', searchTerm.trim());

      // Role-based scoping
      const isTLInExport = user.role !== 'ADMIN' &&
        (user?.designationId === 1 || user?.designationName === 'Team Leader');

      if (user.role === 'ADMIN') {
        if (currentFilters?.floorId)    params.append('floorId', currentFilters.floorId);
        if (currentFilters?.teamId)     params.append('teamId', currentFilters.teamId);
        if (currentFilters?.teamLeadId) params.append('teamLeadId', currentFilters.teamLeadId);
        if (currentFilters?.orderById)  params.append('userId', currentFilters.orderById);
      } else if (isTLInExport) {
        const fId = currentFilters?.floorId || String(user?.floorId || '');
        const tId = currentFilters?.teamId || String(user?.teamId || '');
        if (fId) params.append('floorId', fId);
        if (tId) params.append('teamId', tId);
        if (currentFilters?.orderById) params.append('userId', currentFilters.orderById);
      } else {
        params.append('userId', user.id);
      }

      // Common filters
      if (currentFilters?.leadSource  !== 'All Sources')    params.append('source', currentFilters.leadSource);
      if (currentFilters?.paymentMode !== 'All Modes')      params.append('paymentMode', currentFilters.paymentMode);
      if (currentFilters?.campaign    !== 'All Campaigns')  params.append('campaignName', currentFilters.campaign);
      if (currentFilters?.state       !== 'All States')     params.append('state', currentFilters.state);
      if (currentFilters?.courier     !== 'All Couriers')   params.append('courier', currentFilters.courier);
      if (currentFilters?.category    !== 'All Categories') params.append('category', currentFilters.category);
      if (currentFilters?.orders      !== 'All Orders')     params.append('orderType', currentFilters.orders);
      if (currentFilters?.product     !== 'All Products')   params.append('productName', currentFilters.product);

      const query = `?${params.toString()}`;
      const blob = await ordersApi.exportOrdersExcel(query);

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Orders_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Excel download error:', err);
      alert('Failed to download excel file.');
    } finally {
      setIsDownloading(false);
    }
  };

  const toggleMenu = (id, e) => { e.stopPropagation(); setOpenMenuId(openMenuId === id ? null : id); };
  const handleOrderDetails = (o, e) => { e.stopPropagation(); setOpenMenuId(null); navigate(`/orders/amount-decrease/${o.id}`); };
  const handleOrderHistory = (o, e) => { e.stopPropagation(); setOpenMenuId(null); navigate(`/orders/${o.id}/history`); };
  const handleEditOrder = (o, e) => { e.stopPropagation(); setOpenMenuId(null); navigate(`/orders/${o.id}/edit`); };

  const getStatusStyle = (status) => {
    const s = status?.toUpperCase().replace(/\s+/g, '_');
    switch (s) {
      case 'ORDER_PLACED': case 'VERIFIED': case 'CONFIRMED': case 'DELIVERED':
        return { text: '#258F67', border: '#258F67', bg: '#E6FBF3' };
      case 'REJECTED': case 'CANCELLED': case 'UNDELIVERED': case 'RTO': case 'RETURN':
        return { text: '#FF5B5B', border: '#FF5B5B', bg: '#FFF0F0' };
      case 'DISPATCHED': case 'PICK_UP_COMPLETE': case 'OUT_FOR_DELIVERY': case 'SHIPPING':
        return { text: '#30B7CC', border: '#30B7CC', bg: '#EAFBFF' };
      case 'READY_TO_SHIP': case 'IN_TRANSIT':
        return { text: '#E8B750', border: '#E8B750', bg: '#FFFBE6' };
      case 'PENDING': case 'APPROVAL_PENDING': case 'HOLD': case 'UNCONFIRMED':
        return { text: '#FFA043', border: '#FFA043', bg: '#FFF5EB' };
      default:
        return { text: '#4A5568', border: '#CBD5E0', bg: '#F7FAFC' };
    }
  };

  const StatusBadge = ({ status }) => {
    const { currentTheme } = useTheme();
    const isDark = ['Purple', 'Black'].includes(currentTheme.name);
    const st = getStatusStyle(status);
    const backgroundColor = isDark ? `${st.text}20` : st.bg;
    return (
      <span className="px-3 py-1.5 inline-flex items-center gap-1 rounded-full text-xs font-bold border whitespace-nowrap shadow-sm"
        style={{ color: st.text, borderColor: isDark ? st.text : st.border, backgroundColor }}>
        {formatApprovalStatus(status)}
        {status === 'DELIVERED' && (
          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
        )}
      </span>
    );
  };

  const getEmpIdCode = (order) => {
    const directId = order.createdByEmpId || order.orderByEmp || (order.orderBy && order.orderBy.empId) || order.employeeId || order.empId;
    if (directId && String(directId).match(/[A-Z]/i)) {
      return directId;
    }
    const dbId = order.createdById || order.assignToVerifiedId || order.createdByEmpId || order.empId || (order.orderBy && order.orderBy.id);
    if (dbId) {
      const emp = allEmployeesList.find(e => String(e.id || e.empId) === String(dbId));
      if (emp?.employeeId) return emp.employeeId;
    }
    const creatorName = order.createdByName || order.orderBy;
    if (creatorName) {
      const emp = allEmployeesList.find(e =>
        String(e.name || e.employeeName || e.fullName).toLowerCase() === String(creatorName).toLowerCase()
      );
      if (emp?.employeeId) return emp.employeeId;
    }
    return directId || '—';
  };

  const PersonCell = ({ name, empId }) => (
    <div className="flex flex-col min-w-0">
      <span className="text-sm font-medium text-gray-800 truncate max-w-[120px]">{name || '—'}</span>
      {name && empId && <span className="text-xs text-gray-400">Emp: {empId}</span>}
    </div>
  );

  const hasAnyAction = has('ORDER_DATA_VIEW') || has('ORDERS_EDIT');
  const baseColCount = 12;
  const adminColCount = baseColCount + 7;
  const emptyColSpan = isAdmin
    ? (hasAnyAction ? adminColCount + 1 : adminColCount)
    : (hasAnyAction ? baseColCount + 1 : baseColCount);

  // ── Employee list: team select kiya? teamEmployees use karo, warna sab ──
  const orderByOptions = filterOptions.teamEmployees.length > 0
    ? filterOptions.teamEmployees
    : filterOptions.employees;

  return (
    <div className="space-y-6">

      {/* ── Header ── */}
      <div className="flex items-center justify-between bg-transparent mb-2">
        <div className="flex items-center gap-4">
          <h1 className="text-[28px] font-black text-[#1a3b32]">All Orders</h1>
          {isAdmin && (
            <button
              onClick={() => { setShowBulkUploadModal(true); setBulkUploadFile(null); setBulkJobStatus(null); setBulkJobId(null); }}
              className="flex items-center gap-2 px-4 py-2 text-white rounded-xl text-sm font-semibold shadow-sm hover:shadow-md transition-all"
              style={{ backgroundColor: currentTheme.color }}>
              <Upload className="w-4 h-4" />
              Bulk Upload
            </button>
          )}
          {has('ORDER_DATA_VIEW') && (
            <div className="relative" ref={dateDropdownRef}>
              <button onClick={() => setIsDateDropdownOpen(!isDateDropdownOpen)}
                className="flex items-center bg-white border border-gray-300 rounded-full px-4 py-1.5 shadow-sm cursor-pointer transition-all"
                style={{ borderColor: isDateDropdownOpen ? currentTheme.color : undefined }}>
                <CalendarDays className="w-5 h-5 text-orange-400 mr-2" />
                <span className="text-[15px] font-medium text-gray-400">{selectedDateRangeLabel}</span>
                <ChevronDown className={`w-4 h-4 ml-2 transition-transform ${isDateDropdownOpen ? 'rotate-180' : ''}`} style={{ color: currentTheme.color }} />
              </button>
              {isDateDropdownOpen && (
                <div className="absolute left-0 top-full mt-2 bg-white rounded-2xl shadow-xl border border-gray-100 py-3 z-50 w-56">
                  {dateRangeOptions.map(opt => (
                    <button key={opt.value} onClick={() => handleDateRangeFilter(opt.value, opt.label)}
                      className="w-full px-5 py-2.5 text-left text-[14px] transition-colors"
                      style={{ backgroundColor: selectedDateRange === opt.value ? `${currentTheme.color}15` : 'transparent', color: selectedDateRange === opt.value ? currentTheme.color : '#374151', fontWeight: selectedDateRange === opt.value ? 'bold' : 'normal' }}
                      onMouseEnter={(e) => { if (selectedDateRange !== opt.value) e.currentTarget.style.backgroundColor = `${currentTheme.color}10`; }}
                      onMouseLeave={(e) => { if (selectedDateRange !== opt.value) e.currentTarget.style.backgroundColor = 'transparent'; }}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="flex gap-4">
            <ExpandableSearchBar searchTerm={searchTerm} onSearchChange={(v) => setSearchTerm(v)} placeholder="Search by lead id or customer name" />
            {(selectedStatus || JSON.stringify(activeFilters) !== JSON.stringify(initialFilters)) && (
              <button
                onClick={() => {
                  const resetFilters = getInitialFilters();
                  setSelectedStatus(null); selectedStatusRef.current = null;
                  setActiveFilters(resetFilters); activeFiltersRef.current = resetFilters;
                  setTempFilters(resetFilters);
                  setCurrentPage(1); currentPageRef.current = 1;
                  setSelectedDateRange('today'); selectedDateRangeRef.current = 'today';
                  setSelectedDateRangeLabel('Today');
                  // Admin ke liye dynamic dropdowns reset, TL ke liye nahi
                  if (!isTL) setFilterOptions(prev => ({ ...prev, teams: [], teamEmployees: [] }));
                  fetchOrders(null, 'today', '', '', resetFilters, 0);
                }}
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 transition-colors">
                Clear Filter
              </button>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {has('ORDERS_VIEW') && (
            <button onClick={() => setIsFilterModalOpen(true)}
              className="p-2.5 bg-white border border-gray-200 rounded-full shadow-sm hover:bg-gray-50 transition-all">
              <Filter className="w-6 h-6" style={{ color: currentTheme.color }} />
            </button>
          )}
          {isAdmin && <ExcelExportButton onClick={handleDownloadExcel} isDownloading={isDownloading} />}
        </div>
      </div>

      {/* ── Global Phone Search Banner ── */}
      {isGlobalPhoneSearch && (
        <div
          className="flex items-center justify-between px-4 py-3 rounded-xl border text-sm font-medium"
          style={{ backgroundColor: `${currentTheme.color}12`, borderColor: `${currentTheme.color}40`, color: currentTheme.color }}
        >
          <div className="flex items-center gap-2">
            <span className="text-base">📞</span>
            <span>
              Showing all orders for number&nbsp;
              <span className="font-bold tracking-wide">{globalPhoneNumber}</span>
              &nbsp;— across all agents & teams
            </span>
          </div>
          <button
            onClick={() => { setSearchTerm(''); setIsGlobalPhoneSearch(false); setGlobalPhoneNumber(''); }}
            className="ml-4 p-1 rounded-full hover:bg-black/10 transition-colors"
            title="Clear phone search"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* ── Stat Cards ── */}
      <div className="flex gap-[10px] overflow-x-auto no-scrollbar scroll-smooth pt-6 pb-6 px-4">
        {stats.map((stat, i) => (
          <div key={i} className="min-w-[180px]">
            <OrderStatCard label={stat.label} value={stat.value} color={stat.color}
              icon={stat.icon} isActive={selectedStatus === stat.status}
              onClick={() => handleStatClick(stat.status)} />
          </div>
        ))}
      </div>

      {/* ── Table ── */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader className="w-8 h-8 animate-spin" style={{ color: currentTheme.color }} />
          </div>
        ) : (
          <>
            <div className="overflow-x-auto rounded-t-xl" style={{ backgroundColor: currentTheme.color }}>
              <table className="w-full border-collapse">
                <thead className="text-white">
                  <tr style={{ background: currentTheme.color }}>
                    <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap" style={{ background: currentTheme.color }}>ORDER ID</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap" style={{ background: currentTheme.color }}>DATE</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap" style={{ background: currentTheme.color }}>NAME</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap" style={{ background: currentTheme.color }}>ADDRESS</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap" style={{ background: currentTheme.color }}>PRODUCT</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap" style={{ background: currentTheme.color }}>CATEGORY</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap" style={{ background: currentTheme.color }}>REST AMT.</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap" style={{ background: currentTheme.color }}>PAY MODE</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap" style={{ background: currentTheme.color }}>ORDER TYPE</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap" style={{ background: currentTheme.color }}>STATUS</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap" style={{ background: currentTheme.color }}>STATE</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap" style={{ background: currentTheme.color }}>CAMPAIGN</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap" style={{ background: currentTheme.color }}>ASSIGN TO VERIFIED</th>
                    {!isAdmin && <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap" style={{ background: currentTheme.color }}>EMP ID</th>}
                    <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap" style={{ background: currentTheme.color }}>AWB No.</th>
                    {isAdmin && <>
                      <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap" style={{ background: currentTheme.color }}>LEAD SOURCE</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap" style={{ background: currentTheme.color }}>C. PARTNER</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap" style={{ background: currentTheme.color }}>COURIER</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap" style={{ background: currentTheme.color }}>ORDER BY</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap" style={{ background: currentTheme.color }}>EMP ID</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap" style={{ background: currentTheme.color }}>VERIFIED BY</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap" style={{ background: currentTheme.color }}>TEAM LEAD</th>
                    </>}
                    <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap min-w-[200px]" style={{ background: currentTheme.color }}>REMARK</th>
                    {hasAnyAction && <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap" style={{ background: currentTheme.color }}>ACTION</th>}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {orders.length > 0 ? orders.map((order, index) => (
                    <tr key={order.id || index} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-4 text-sm font-medium text-gray-900 whitespace-nowrap">{order.orderId}</td>
                      <td className="px-4 py-4 text-sm text-gray-700 whitespace-nowrap">{formatDate(order.orderDate)}</td>
                      <td className="px-4 py-4">
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-gray-900">{order.customerName}</span>
                          <div className="flex items-center space-x-1 text-xs text-gray-500">
                            <Phone className="w-3 h-3" />
                            <span>{order.contactNumber}</span>
                            {order.whatsappSent && <img src={icon} alt="WA" className="w-4 h-4" />}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-700">
                        <span className="block max-w-[200px] truncate" title={
                          order.address
                          || order.fullAddress
                          || [order.houseNumber, order.streetName, order.colonyName, order.landmark, order.cityTown || order.city, order.district, order.state, order.areaPinCode].filter(Boolean).join(', ')
                        }>
                          {order.address
                            || order.fullAddress
                            || [order.houseNumber, order.streetName, order.colonyName, order.landmark, order.cityTown || order.city, order.district, order.state, order.areaPinCode].filter(Boolean).join(', ')
                            || '-/-'}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-700">{order.productName || (order.orderItems?.length > 0 ? order.orderItems.map(i => i.productName).join(', ') : 'N/A')}</td>
                      <td className="px-4 py-4 text-sm text-gray-700 whitespace-nowrap">{order.category || order.categoryName || order.productCategory || order.orderItems?.[0]?.category || order.orderItems?.[0]?.categoryName || '—'}</td>
                      <td className="px-4 py-4 text-sm font-semibold text-green-600 whitespace-nowrap">{formatCurrency(order.restAmount)}</td>
                      <td className="px-4 py-4 text-sm text-gray-700 whitespace-nowrap">{order.paymentMode === 'ONLINE' ? 'Online' : order.paymentMode === 'CASH' || order.paymentMode === 'COD' ? 'COD' : order.paymentMode || 'COD'}</td>
                      <td className="px-4 py-4 text-sm text-gray-700 whitespace-nowrap">{order.orderType || 'Fresh Order'}</td>
                      <td className="px-4 py-4"><StatusBadge status={order.status} /></td>
                      <td className="px-4 py-4 text-sm text-gray-700 whitespace-nowrap">{order.state || order.from || '—'}</td>
                      <td className="px-4 py-4 text-sm text-gray-700 whitespace-nowrap">{order.campaign || order.campaignName || '—'}</td>
                      <td className="px-4 py-4"><PersonCell name={order.assignedTo || order.assignToVerified || order.assignedToVerified || order.assignToVerifiedName || '—'} empId={order.assignToVerifiedId || null} /></td>
                      {!isAdmin && <td className="px-4 py-4 text-sm text-gray-700 whitespace-nowrap font-mono">{getEmpIdCode(order)}</td>}
                      <td className="px-4 py-4 text-sm text-gray-700 whitespace-nowrap">
                        {order.awbNumber ? <span className="flex items-center gap-1">{order.awbNumber}<svg className="w-3.5 h-3.5 text-teal-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path d="M8 3a1 1 0 000 2h2a1 1 0 100-2H8zM6 5a1 1 0 00-1 1v9a1 1 0 001 1h8a1 1 0 001-1V6a1 1 0 00-1-1h-1V3a3 3 0 00-6 0v2H6z" /></svg></span> : '—'}
                      </td>
                      {isAdmin && <>
                        <td className="px-4 py-4 text-sm text-gray-700 whitespace-nowrap">{order.leadSourceDisplay || '—'}</td>
                        <td className="px-4 py-4 text-sm text-gray-700 whitespace-nowrap">{order.courierPartner || '—'}</td>
                        <td className="px-4 py-4 text-sm text-gray-700 whitespace-nowrap">{order.courierName || '—'}</td>
                        <td className="px-4 py-4"><PersonCell name={order.createdByName || order.orderBy} empId={getEmpIdCode(order)} /></td>
                        <td className="px-4 py-4 text-sm text-gray-700 whitespace-nowrap font-mono">{getEmpIdCode(order)}</td>
                        <td className="px-4 py-4"><PersonCell name={order.verifiedByName || '—'} empId={order.verifiedByName ? (order.verifiedById || order.verifiedByEmpId) : null} /></td>
                        <td className="px-4 py-4"><PersonCell name={order.teamLeadName || order.teamName} empId={order.teamLeadEmpId} /></td>
                      </>}
                      <td className="px-4 py-4 text-sm text-gray-700 min-w-[200px]">
                        <span className="block w-full max-w-[250px]" style={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }} title={order.remarks}>{order.remarks || '—'}</span>
                      </td>
                      {hasAnyAction && (
                        <td className="px-4 py-4 relative">
                          <div className="flex items-center space-x-2 action-menu-container">
                            {has('ORDER_DATA_VIEW') && (
                              <button onClick={(e) => { e.stopPropagation(); handleOrderDetails(order, e); }}
                                className="p-1.5 rounded-lg transition-colors bg-opacity-10 hover:bg-opacity-20"
                                style={{ backgroundColor: currentTheme.color }} title="View">
                                <Eye className="w-5 h-5 text-white" />
                              </button>
                            )}
                            {has('ORDERS_EDIT') && (
                              <button onClick={(e) => toggleMenu(order.id, e)} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
                                <MoreVertical className="w-5 h-5 text-gray-600" />
                              </button>
                            )}
                          </div>
                          {openMenuId === order.id && (
                            <div className="action-menu-container absolute right-0 top-12 bg-white rounded-lg shadow-lg border border-gray-200 py-2 w-48 z-10">
                              {has('ORDER_DATA_VIEW') && <button onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); handleOrderDetails(order, e); }} className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center space-x-2"><FileText className="w-4 h-4 text-gray-600" /><span>Order details</span></button>}
                              {has('ORDER_DATA_VIEW') && <button onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); handleOrderHistory(order, e); }} className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center space-x-2"><History className="w-4 h-4 text-gray-600" /><span>Order History</span></button>}
                              {has('ORDERS_EDIT') && <button onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); handleEditOrder(order, e); }} className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center space-x-2"><Edit className="w-4 h-4 text-gray-600" /><span>Edit Order</span></button>}
                            </div>
                          )}
                        </td>
                      )}
                    </tr>
                  )) : (
                    <tr><td colSpan={emptyColSpan} className="px-4 py-8 text-center text-gray-500">No orders found</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            <Pagination
              currentPage={currentPage}
              totalItems={totalCount}
              itemsPerPage={PAGE_SIZE}
              onPageChange={(p) => {
                setCurrentPage(p); currentPageRef.current = p;
                window.scrollTo({ top: 0, behavior: 'smooth' });
                fetchOrders(selectedStatusRef.current, selectedDateRangeRef.current, customStartDateRef.current, customEndDateRef.current, activeFiltersRef.current, p - 1);
              }}
            />
          </>
        )}
      </div>

      {/* ── Filter Modal ── */}
      {isFilterModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl w-full max-w-4xl mx-4 flex flex-col shadow-xl max-h-[90vh]">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200" style={{ background: currentTheme.color }}>
              <h2 className="text-lg font-bold text-white">Filter Orders</h2>
              <button onClick={() => setIsFilterModalOpen(false)} className="p-1 hover:bg-white/20 rounded transition-colors"><X className="w-5 h-5 text-white" /></button>
            </div>
            <div className="p-6 pb-64 overflow-y-auto">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 laptop:grid-cols-4 desktop:grid-cols-5 desktop-xl:grid-cols-8 gap-x-8 gap-y-5 text-left relative">

                {/* ── Admin only: Floor → Team → Employee (dynamic) ── */}
                {isAdmin && (
                  <>
                    {/* Floor */}
                    <div className="col-span-1">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Floor</label>
                      <ThemeSelect
                        value={
                          tempFilters.floorId
                            ? filterOptions.floors.find(f => f.floorId === tempFilters.floorId)?.floorName || 'All Floors'
                            : 'All Floors'
                        }
                        onChange={(v) => {
                          if (v === 'All Floors') {
                            setTempFilters(prev => ({ ...prev, floorId: '', teamId: '', orderBy: 'Select User', orderById: '' }));
                            setFilterOptions(prev => ({ ...prev, teams: [], teamEmployees: [] }));
                          } else {
                            handleFloorChange(v);
                          }
                        }}
                        options={['All Floors', ...filterOptions.floors.map(f => f.floorName)]}
                        className="w-full"
                        icon={User}
                      />
                    </div>

                    {/* Team — floor ke baad enable */}
                    <div className="col-span-1">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Team
                        {teamsLoading && <span className="ml-1 text-xs text-gray-400">(loading...)</span>}
                      </label>
                      <ThemeSelect
                        value={
                          tempFilters.teamId
                            ? filterOptions.teams.find(t => t.teamId === tempFilters.teamId)?.teamName || 'All Teams'
                            : 'All Teams'
                        }
                        onChange={(v) => {
                          if (v === 'All Teams') {
                            setTempFilters(prev => ({ ...prev, teamId: '', orderBy: 'Select User', orderById: '' }));
                            setFilterOptions(prev => ({ ...prev, teamEmployees: [] }));
                          } else {
                            handleTeamChange(v, tempFilters.floorId);
                          }
                        }}
                        options={['All Teams', ...filterOptions.teams.map(t => t.teamName)]}
                        className="w-full"
                        icon={User}
                      />
                    </div>

                    {/* Order By — team ke employees ya sab */}
                    <div className="col-span-1">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Order By
                        {empLoading && <span className="ml-1 text-xs text-gray-400">(loading...)</span>}
                      </label>
                      <ThemeSelect
                        value={tempFilters.orderBy}
                        onChange={(v) => {
                          const emp = orderByOptions.find(e => e.name === v);
                          setTempFilters(prev => ({ ...prev, orderBy: v, orderById: emp?.empId || '' }));
                        }}
                        options={['Select User', ...orderByOptions.map(e => e.name)]}
                        className="w-full"
                        icon={User}
                      />
                    </div>
                  </>
                )}

                {/* ── TL only: sirf apni team ke employees dikho, floor/team hidden ── */}
                {isTL && (
                  <div className="col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Order By
                      {empLoading && <span className="ml-1 text-xs text-gray-400">(loading...)</span>}
                    </label>
                    <ThemeSelect
                      value={tempFilters.orderBy}
                      onChange={(v) => {
                        const emp = filterOptions.teamEmployees.find(e => e.name === v);
                        setTempFilters(prev => ({ ...prev, orderBy: v, orderById: emp?.empId || '' }));
                      }}
                      options={['Select User', ...filterOptions.teamEmployees.map(e => e.name)]}
                      className="w-full"
                      icon={User}
                    />
                  </div>
                )}

                {/* Static filters */}
                <div className="col-span-1"><label className="block text-sm font-medium text-gray-700 mb-1">Orders</label><ThemeSelect value={tempFilters.orders} onChange={(v) => setTempFilters({ ...tempFilters, orders: v })} options={['All Orders', 'Fresh Order', 'Repeat Order']} className="w-full" /></div>
                <div className="col-span-1"><label className="block text-sm font-medium text-gray-700 mb-1">Courier</label><ThemeSelect value={tempFilters.courier} onChange={(v) => setTempFilters({ ...tempFilters, courier: v })} options={['All Couriers', ...filterOptions.couriers]} className="w-full" icon={Truck} /></div>
                <div className="col-span-1"><label className="block text-sm font-medium text-gray-700 mb-1">Category</label><ThemeSelect value={tempFilters.category} onChange={(v) => setTempFilters({ ...tempFilters, category: v })} options={['All Categories', ...filterOptions.categories]} className="w-full" icon={Tag} /></div>
                <div className="col-span-1"><label className="block text-sm font-medium text-gray-700 mb-1">State</label><ThemeSelect value={tempFilters.state} onChange={(v) => setTempFilters({ ...tempFilters, state: v })} options={['All States', ...filterOptions.states]} className="w-full" /></div>
                <div className="col-span-1"><label className="block text-sm font-medium text-gray-700 mb-1">Lead Source</label><ThemeSelect value={tempFilters.leadSource} onChange={(v) => setTempFilters({ ...tempFilters, leadSource: v })} options={['All Sources', ...filterOptions.leadSources]} className="w-full" /></div>
                <div className="col-span-1"><label className="block text-sm font-medium text-gray-700 mb-1">Payment Mode</label><ThemeSelect value={tempFilters.paymentMode} onChange={(v) => setTempFilters({ ...tempFilters, paymentMode: v })} options={['All Modes', ...filterOptions.paymentModes]} className="w-full" /></div>
                <div className="col-span-1"><label className="block text-sm font-medium text-gray-700 mb-1">Campaign</label><ThemeSelect value={tempFilters.campaign} onChange={(v) => setTempFilters({ ...tempFilters, campaign: v })} options={['All Campaigns', ...filterOptions.campaigns]} className="w-full" icon={Layout} /></div>
                <div className="col-span-1"><label className="block text-sm font-medium text-gray-700 mb-1">Product</label><ThemeSelect value={tempFilters.product} onChange={(v) => setTempFilters({ ...tempFilters, product: v })} options={['All Products', ...filterOptions.products]} className="w-full" icon={Tag} /></div>
              </div>
            </div>
            <div className="flex justify-end px-6 py-4 border-t border-gray-200 space-x-4 bg-gray-50">
              <button
                onClick={() => {
                  const resetFilters = getInitialFilters();
                  setTempFilters(resetFilters);
                  // Admin ke liye dynamic dropdowns reset, TL ke liye nahi
                  if (!isTL) setFilterOptions(prev => ({ ...prev, teams: [], teamEmployees: [] }));
                }}
                className="px-6 py-3 text-[15px] font-semibold text-gray-600 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors">
                Reset
              </button>
              <button
                onClick={() => {
                  setActiveFilters(tempFilters);
                  activeFiltersRef.current = tempFilters;
                  setCurrentPage(1);
                  currentPageRef.current = 1;
                  setIsFilterModalOpen(false);
                  fetchOrders(selectedStatusRef.current, selectedDateRangeRef.current, customStartDateRef.current, customEndDateRef.current, tempFilters, 0);
                }}
                className="px-6 py-3 text-[15px] font-semibold text-white rounded-xl transition-colors hover:shadow-md" style={{ backgroundColor: currentTheme.color }}>
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Custom Date Modal ── */}
      {showCustomDateModal && (
        <div
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowCustomDateModal(false);
          }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
        >
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 animate-scaleIn">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-800">Select Custom Date Range</h3>
              <button
                onClick={() => setShowCustomDateModal(false)}
                className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Start Date – End Date</label>
              <RangePicker
                value={
                  customStartDate && customEndDate
                    ? [dayjs(customStartDate), dayjs(customEndDate)]
                    : null
                }
                onChange={(dates) => {
                  if (dates && dates[0] && dates[1]) {
                    setCustomStartDate(dates[0].format('YYYY-MM-DD'));
                    setCustomEndDate(dates[1].format('YYYY-MM-DD'));
                  } else {
                    setCustomStartDate('');
                    setCustomEndDate('');
                  }
                }}
                format="DD-MM-YYYY"
                style={{ width: '100%', padding: '8px 12px', borderRadius: '10px' }}
                className="w-full text-base"
                size="large"
              />
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowCustomDateModal(false)}
                className="px-5 py-2 text-sm font-semibold text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCustomDateApply}
                disabled={!customStartDate || !customEndDate}
                className="px-5 py-2 text-sm font-semibold text-white rounded-xl transition-colors hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: currentTheme.color }}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Bulk Upload Modal ── */}
      {showBulkUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">

            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4" style={{ background: currentTheme.color }}>
              <div className="flex items-center gap-3">
                <Upload className="w-5 h-5 text-white" />
                <h2 className="text-lg font-bold text-white">Bulk Order Upload</h2>
              </div>
              <button
                onClick={() => {
                  setShowBulkUploadModal(false);
                  if (bulkPollRef.current) clearInterval(bulkPollRef.current);
                  setBulkPolling(false);
                }}
                className="p-1 hover:bg-white/20 rounded transition-colors">
                <X className="w-5 h-5 text-white" />
              </button>
            </div>

            <div className="p-6 space-y-5">

              {/* Template Download */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl border border-gray-200">
                <div>
                  <p className="text-sm font-semibold text-gray-700">Sample Template</p>
                  <p className="text-xs text-gray-400">With the correct columns in CSV format</p>
                </div>
                <button
                  onClick={handleDownloadTemplate}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-white rounded-lg transition-colors"
                  style={{ backgroundColor: currentTheme.color }}>
                  <Download className="w-4 h-4" />
                  Download
                </button>
              </div>

              {/* File Upload Area */}
              {!bulkJobId && (
                <div
                  className="border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all hover:border-opacity-80"
                  style={{ borderColor: bulkUploadFile ? currentTheme.color : '#D1D5DB' }}
                  onClick={() => document.getElementById('bulkFileInput').click()}>
                  <input
                    id="bulkFileInput"
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    className="hidden"
                    onChange={handleBulkFileSelect}
                  />
                  {bulkUploadFile ? (
                    <div className="space-y-2">
                      <CheckCircle className="w-10 h-10 mx-auto" style={{ color: currentTheme.color }} />
                      <p className="text-sm font-semibold text-gray-700">{bulkUploadFile.name}</p>
                      <p className="text-xs text-gray-400">{(bulkUploadFile.size / 1024).toFixed(1)} KB</p>
                      <p className="text-xs" style={{ color: currentTheme.color }}>Click to change file</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Upload className="w-10 h-10 mx-auto text-gray-300" />
                      <p className="text-sm font-semibold text-gray-600">Drag a CSV or Excel file here, or click to browse</p>
                      <p className="text-xs text-gray-400">.csv, .xlsx, .xls supported</p>
                    </div>
                  )}
                </div>
              )}

              {/* Progress Section */}
              {bulkJobStatus && (
                <div className="space-y-3">

                  {/* Status Badge */}
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-gray-700">Job ID: {bulkJobId}</span>
                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                      bulkJobStatus.status === 'COMPLETED' ? 'bg-green-100 text-green-700' :
                      bulkJobStatus.status === 'FAILED' ? 'bg-red-100 text-red-700' :
                      bulkJobStatus.status === 'PROCESSING' ? 'bg-blue-100 text-blue-700' :
                      'bg-yellow-100 text-yellow-700'
                    }`}>
                      {bulkJobStatus.status}
                    </span>
                  </div>

                  {/* Progress Bar */}
                  {['PROCESSING', 'QUEUED'].includes(bulkJobStatus.status) && (
                    <div className="space-y-1">
                      <div className="flex justify-between text-xs text-gray-500">
                        <span>{bulkJobStatus.processedRows || 0} / {bulkJobStatus.totalRows || 0} rows</span>
                        <span>{bulkJobStatus.progressPercent || 0}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2.5">
                        <div
                          className="h-2.5 rounded-full transition-all duration-500"
                          style={{ width: `${bulkJobStatus.progressPercent || 0}%`, backgroundColor: currentTheme.color }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Stats Grid */}
                  {bulkJobStatus.status === 'COMPLETED' && (
                    <div className="grid grid-cols-3 gap-3">
                      <div className="text-center p-3 bg-green-50 rounded-xl border border-green-100">
                        <p className="text-lg font-black text-green-600">{bulkJobStatus.successRows || 0}</p>
                        <p className="text-xs text-green-500 font-medium">Success</p>
                      </div>
                      <div className="text-center p-3 bg-red-50 rounded-xl border border-red-100">
                        <p className="text-lg font-black text-red-600">{bulkJobStatus.failedRows || 0}</p>
                        <p className="text-xs text-red-500 font-medium">Failed</p>
                      </div>
                      <div className="text-center p-3 bg-yellow-50 rounded-xl border border-yellow-100">
                        <p className="text-lg font-black text-yellow-600">{bulkJobStatus.skippedRows || 0}</p>
                        <p className="text-xs text-yellow-500 font-medium">Skipped</p>
                      </div>
                    </div>
                  )}

                  {/* Error message */}
                  {bulkJobStatus.status === 'FAILED' && bulkJobStatus.errorSummary && (
                    <div className="flex items-start gap-2 p-3 bg-red-50 rounded-xl border border-red-100">
                      <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-red-600">{bulkJobStatus.errorSummary}</p>
                    </div>
                  )}

                  {/* Polling indicator */}
                  {bulkPolling && (
                    <div className="flex items-center gap-2 text-xs text-gray-400">
                      <Clock className="w-3 h-3 animate-spin" />
                      <span>Processing... auto-refreshing</span>
                    </div>
                  )}

                  {/* Upload another */}
                  {['COMPLETED', 'FAILED'].includes(bulkJobStatus.status) && (
                    <button
                      onClick={() => { setBulkUploadFile(null); setBulkJobId(null); setBulkJobStatus(null); }}
                      className="w-full py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors">
                      Upload New File
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            {!bulkJobId && (
              <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50">
                <button
                  onClick={() => setShowBulkUploadModal(false)}
                  className="px-5 py-2.5 text-sm font-semibold text-gray-600 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors">
                  Cancel
                </button>
                <button
                  onClick={handleBulkUpload}
                  disabled={!bulkUploadFile || bulkUploading}
                  className="px-5 py-2.5 text-sm font-semibold text-white rounded-xl transition-all hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  style={{ backgroundColor: currentTheme.color }}>
                  {bulkUploading ? (
                    <><Loader className="w-4 h-4 animate-spin" />Uploading...</>
                  ) : (
                    <><Upload className="w-4 h-4" />Upload</>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function OrderStatCard({ label, value, color, icon, isActive, onClick }) {
  const { currentTheme } = useTheme();
  const isDark = ['Purple', 'Black'].includes(currentTheme.name);
  const colorMap = {
    blue: { bg: isDark ? 'bg-blue-900/20' : 'bg-[#F0F7FF]', border: isDark ? 'border-blue-500/30' : 'border-[#E0EFFF]', ring: 'ring-blue-400', inner: 'shadow-[inset_0px_0px_15px_rgba(37,99,235,0.15)]' },
    green: { bg: isDark ? 'bg-emerald-900/20' : 'bg-[#F0FDF4]', border: isDark ? 'border-emerald-500/30' : 'border-[#DCFCE7]', ring: 'ring-emerald-400', inner: 'shadow-[inset_0px_0px_15px_rgba(5,150,105,0.15)]' },
    yellow: { bg: isDark ? 'bg-amber-900/20' : 'bg-[#FFFBEB]', border: isDark ? 'border-amber-500/30' : 'border-[#FEF3C7]', ring: 'ring-amber-400', inner: 'shadow-[inset_0px_0px_15px_rgba(217,119,6,0.15)]' },
    orange: { bg: isDark ? 'bg-orange-900/20' : 'bg-[#FFF7ED]', border: isDark ? 'border-orange-500/30' : 'border-[#FFEDD5]', ring: 'ring-orange-400', inner: 'shadow-[inset_0px_0px_15px_rgba(234,88,12,0.15)]' },
    black: { bg: isDark ? 'bg-gray-800/20' : 'bg-[#F9FAFB]', border: isDark ? 'border-gray-500/30' : 'border-[#F3F4F6]', ring: 'ring-gray-400', inner: 'shadow-[inset_0px_0px_15px_rgba(55,65,81,0.15)]' },
    pink: { bg: isDark ? 'bg-rose-900/20' : 'bg-[#FFF1F2]', border: isDark ? 'border-rose-500/30' : 'border-[#FFE4E6]', ring: 'ring-rose-400', inner: 'shadow-[inset_0px_0px_15px_rgba(225,29,72,0.15)]' },
    cyan: { bg: isDark ? 'bg-cyan-900/20' : 'bg-[#ECFEFF]', border: isDark ? 'border-cyan-500/30' : 'border-[#CFFAFE]', ring: 'ring-cyan-400', inner: 'shadow-[inset_0px_0px_15px_rgba(8,145,178,0.15)]' },
    teal: { bg: isDark ? 'bg-teal-900/20' : 'bg-[#F0FDFA]', border: isDark ? 'border-teal-500/30' : 'border-[#CCFBF1]', ring: 'ring-teal-400', inner: 'shadow-[inset_0px_0px_15px_rgba(13,148,136,0.15)]' },
    purple: { bg: isDark ? 'bg-indigo-900/20' : 'bg-[#F5F3FF]', border: isDark ? 'border-indigo-500/30' : 'border-[#EDE9FE]', ring: 'ring-indigo-400', inner: 'shadow-[inset_0px_0px_15px_rgba(124,58,237,0.15)]' },
  };
  const style = colorMap[color] || colorMap.blue;
  return (
    <div onClick={onClick}
      className={`relative ${style.bg} ${style.border} border-2 rounded-[24px] flex flex-col items-center justify-center w-full h-[150px] shadow-sm ${style.inner} cursor-pointer transition-all hover:shadow-xl hover:-translate-y-1 group overflow-hidden ${isActive ? `ring-2 ${style.ring}` : ''}`}>
      <div className="mb-0 w-28 h-20 flex items-center justify-center relative translate-y-[-2px]">{icon}</div>
      <p className={`text-[11px] font-black ${isDark ? 'text-gray-400' : 'text-gray-400'} mb-1.5 uppercase tracking-widest text-center select-none`}>{label}</p>
      <div className="flex items-baseline gap-1.5">
        <span className="text-[32px] font-black leading-none select-none" style={{ color: isDark ? '#FFFFFF' : currentTheme.color }}>{value}</span>
        <span className={`text-[12px] font-bold ${isDark ? 'text-gray-400' : 'text-gray-400'} select-none uppercase`}>orders</span>
      </div>
    </div>
  );
}

export default OrdersList;